import { AnchorButton, Intent, Position } from '@blueprintjs/core';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'vet-bones/components';

import { exportProject } from 'src/actions/projects/action-creators/exportProject';
import { TEST_IDS } from 'src/constants/testIds';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectSelector } from 'src/selectors/project';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { confirmAcceptInvalidMappings } from 'src/utils/export/confirmAcceptInvalidMappings';
import { getInvalidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDiagramSnapshot } from 'src/utils/hooks/useDiagramSnapshot';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectNavbarButtons: React.VFC = () => {
  const dispatch = useDispatch();
  const diagramSnapshot = useDiagramSnapshot();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));

  const connection = useDefaultPortalConnection();

  const getUserConfirmationForInvalidMappings = async () => {
    if (!diagramSnapshot || !project) {
      return false;
    }
    const invalidMappingsByResource = getInvalidMappingsByResource(
      diagramSnapshot.model
    );
    const shouldPublish = await confirmAcceptInvalidMappings({
      invalidMappingsByResource,
      fileUploadsById: project.fileUploadsById,
      virtualGraphsById: project.virtualGraphsById,
    });
    return shouldPublish;
  };

  const handleSettings = () => {
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: true }));
  };

  const canExport = Boolean(diagramSnapshot && project && !isDraftDiagram);
  const handleExport = async () => {
    if (!canExport) {
      return;
    }
    const shouldExport = await getUserConfirmationForInvalidMappings();
    if (!shouldExport) {
      return;
    }
    dispatch(exportProject(projectId, connection));
  };

  const handlePublish = async () => {
    if (!canExport) {
      return;
    }
    const shouldExport = await getUserConfirmationForInvalidMappings();
    if (!shouldExport) {
      return;
    }
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: true }));
  };

  return (
    <div className="sd-project-navbar-buttons">
      <AnchorButton
        data-testid={TEST_IDS.projectNavbar.settings}
        intent={Intent.PRIMARY}
        minimal
        onClick={handleSettings}
        outlined
        text={copy.components.project.navbar.settings}
      />
      <Tooltip
        content={
          isDraftDiagram
            ? copy.components.voicebox.disabledFor.export
            : copy.components.project.navbar.exportHover
        }
        position={Position.TOP_LEFT}
      >
        <AnchorButton
          data-testid={TEST_IDS.projectNavbar.export.zip}
          disabled={!canExport}
          intent={Intent.PRIMARY}
          minimal
          onClick={handleExport}
          outlined
          text={copy.components.project.navbar.export}
        />
      </Tooltip>
      <Tooltip
        content={
          isDraftDiagram
            ? copy.components.voicebox.disabledFor.publish
            : copy.components.project.navbar.publishHover
        }
        position={Position.TOP_LEFT}
      >
        <AnchorButton
          data-testid={TEST_IDS.projectNavbar.export.publish}
          disabled={!canExport}
          intent={Intent.PRIMARY}
          onClick={handlePublish}
          text={copy.components.project.navbar.publish}
        />
      </Tooltip>
    </div>
  );
};
